/* eslint-disable no-nested-ternary */
import { NextPage } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { getWhiteLabelPageTitle } from 'utils/whiteLabelConfig';

import BankDisclosuresModal from 'components/app-components/wallet/BankDisclosuresModal';
import BridgeCardPinSetupModal from 'components/app-components/wallet/BridgeCardPinSetupModal';
import RequiredDocumentsBanner, { RequirementGroup } from 'components/app-components/wallet/RequiredDocumentsBanner';
import WalletWithdrawalMethodsAndBalance from 'components/app-components/wallet/WalletWithdrawalMethodsAndBalance';
import { AddFundsToCardModal } from 'components/app-components/wallet/card/AddFundsToCardModal';
import { AddToWalletModal } from 'components/app-components/wallet/card/AddToWalletModal';
import { CardDetailsDisplay } from 'components/app-components/wallet/card/CardDetailsDisplay';
import { StripeIssuingCardDetailsDisplay } from 'components/app-components/wallet/card/StripeIssuingCardDetailsDisplay';
import { TransactionsList } from 'components/app-components/wallet/card/TransactionsList';
import AuthorizationCheck from 'components/container-components/allowedRoutes';
import withLayout from 'components/container-components/layout/withLayout';
import Button, { btnClassesBySize, btnClassesByVariant } from 'components/app-components/ui/button';
import theraToast from 'components/generic-components/toast/TheraToast';
import { useUser } from 'context/user';
import { useBridgeCardAccount, useBridgeCustomerDetails, useBridgeGetWallet } from 'hooks/useBridgeSWR';
import { useTheraFernClient } from 'hooks/useFern';
import useReadOnlyContractor from 'hooks/useOceansContractorReadOnly';
import { useCardTransactions } from 'hooks/wallet/useCardTransactions';
import { useSecureCardDetails } from 'hooks/wallet/useSecureCardDetails';
import LoadingSpinner from 'icons/LoadingSpinner';
import { getMissingBridgeRequirements, getSourceOfFundsQuestionnaireUrl } from 'lib/bridgeConstants';
import { ContractorRoutes } from 'routes';
import { displayAddress } from 'utils/address';
import { formatCardExpiryDate } from 'utils/dateTime';
import { formatErrorMessage } from 'utils/error';
import { canUserCreateBridgeCard, isSupportAdminMode } from 'utils/user';

const Wallet: NextPage = () => {
  const { user, isLoading: isUserLoading, mutate: fetchUserProfile } = useUser();
  const { bridgeUserDetails } = user || {};
  const { theraFernApi } = useTheraFernClient();
  const { bridgeCustomer, isPending: isBridgeCustomerPending } = useBridgeCustomerDetails({
    bridgeCustomerID: bridgeUserDetails?.customerId,
  });
  // Read-only contractors cannot create wallet or perform wallet actions
  const { isReadOnlyContractor, readOnlyTooltipMessage } = useReadOnlyContractor();
  const hasBridgeAccount = bridgeUserDetails?.customerId;
  const isWalletCreated = Boolean(bridgeUserDetails?.wallet?.id);
  const hasBridgeWallet = hasBridgeAccount && isWalletCreated;
  const isWalletActive = hasBridgeWallet && bridgeUserDetails?.onboardingStatus === 'active';
  const isOnboardingRejected = bridgeUserDetails?.onboardingStatus === 'rejected';
  const isOnboardingUnderReview = bridgeUserDetails?.onboardingStatus === 'under_review';
  const pendingStatuses = ['awaiting_questionnaire', 'awaiting_ubo', 'incomplete'];
  const isOnboardingPending = pendingStatuses.includes(bridgeUserDetails?.onboardingStatus);
  // Incomplete is user-recoverable: they dropped off mid-KYC and can resume via the Bridge link.
  const isOnboardingIncomplete = bridgeUserDetails?.onboardingStatus === 'incomplete';
  const isOnboardingNotStarted = bridgeUserDetails?.onboardingStatus === 'not_started';
  const [_kycLink, setKycLink] = useState<string | null>(null);
  const stripeCardAccount = bridgeUserDetails?.stripeCardAccount;
  const hasStripeCardAccount = Boolean(stripeCardAccount?.id);
  const legacyCardAccountId = bridgeUserDetails?.cardAccount?.id;
  const isLegacyCardCreated = Boolean(legacyCardAccountId);
  const isCardCreated = isLegacyCardCreated || hasStripeCardAccount;
  // Stripe cards take precedence
  const { cardAccount } = useBridgeCardAccount({
    customerId: bridgeUserDetails?.customerId,
    cardAccountId: hasStripeCardAccount ? undefined : legacyCardAccountId,
  });
  const cardPinStatus = cardAccount?.cardDetails?.pinStatus;
  const isPinNotSet =
    !hasStripeCardAccount && isLegacyCardCreated && cardPinStatus !== undefined && cardPinStatus !== 'ok';
  // Shown after card creation to prompt user to set up Bridge card PIN (lifted here so modal stays visible after user refetch)
  const [showPinSetupModal, setShowPinSetupModal] = useState(false);
  const [showAddToWalletModal, setShowAddToWalletModal] = useState(false);
  const [isPinSetupLoading, setIsPinSetupLoading] = useState(false);
  // https://apidocs.bridge.xyz/api-reference/customers/get-a-single-customer-object#response-endorsements-items-name
  const isCardEndorsed =
    bridgeCustomer?.endorsements?.find((endorsement) => endorsement.name === 'cards')?.status === 'approved';
  const { identity: identityRequirements, sourceOfFunds: sourceOfFundsRequirements } =
    getMissingBridgeRequirements(bridgeCustomer);
  const sourceOfFundsUrl = getSourceOfFundsQuestionnaireUrl(bridgeUserDetails?.customerId);
  const [isVerifyingDocuments, setIsVerifyingDocuments] = useState(false);

  const isUserCountrySupported = useMemo(() => {
    const userCountry = user?.employeeInformation?.country || user?.employeeInformation?.address?.country;
    if (!userCountry) return false;
    return canUserCreateBridgeCard(user?.employeeInformation?.address);
  }, [user?.employeeInformation?.address, user?.employeeInformation?.country]);

  const retryOnboarding = async () => {
    try {
      const res = await theraFernApi.thera.bridge.bridgeApi.retrieveCustomerKycLink(user.userID);
      setKycLink(res.url);
      // open in new tab
      window.open(res.url, '_blank');
    } catch (error) {
      console.error(error);
    }
  };

  const handleVerifyDocuments = async () => {
    try {
      setIsVerifyingDocuments(true);
      const res = await theraFernApi.thera.bridge.bridgeApi.retrieveCardsKycLink(user?.userID);
      window.open(res.url, '_blank');
    } catch (error) {
      theraToast.error({
        message: formatErrorMessage(error, 'Failed to retrieve verification link'),
        options: { id: 'failed-to-retrieve-docs-kyc-link' },
      });
    } finally {
      setIsVerifyingDocuments(false);
    }
  };

  const verificationGroups: RequirementGroup[] = [
    ...(identityRequirements.length > 0
      ? [
          {
            title: 'Identity details',
            items: identityRequirements,
            actionLabel: 'Verify identity details',
            onAction: handleVerifyDocuments,
            isLoading: isVerifyingDocuments,
          },
        ]
      : []),
    ...(sourceOfFundsRequirements.length > 0 && sourceOfFundsUrl
      ? [
          {
            title: 'Source of funds',
            items: sourceOfFundsRequirements,
            actionLabel: 'Complete source of funds',
            onAction: () => window.open(sourceOfFundsUrl, '_blank'),
          },
        ]
      : []),
  ];
  const hasMissingRequirements = verificationGroups.length > 0;

  const showWalletStatusText =
    bridgeUserDetails &&
    (isOnboardingRejected ||
      isOnboardingPending ||
      isOnboardingUnderReview ||
      isOnboardingNotStarted ||
      (!isWalletCreated && hasBridgeAccount)); // Only show "under processing" if they have bridge account but no wallet
  const getWalletStatusText = () => {
    // Don't show any status if bridgeUserDetails is null
    if (!bridgeUserDetails) {
      return null;
    }

    if (isOnboardingRejected) {
      const rejectionReason = bridgeUserDetails?.rejectionReason;
      const hasValidRejectionReason = rejectionReason && rejectionReason.trim() !== '';
      return hasValidRejectionReason
        ? `Your Thera wallet application was rejected. Please try again. Rejection Reason: ${rejectionReason}`
        : 'Your Thera wallet application was rejected. Please try again.';
    }
    if (isOnboardingIncomplete) {
      return 'Your Thera wallet verification is incomplete. Please resume to continue.';
    }
    if (isOnboardingPending) {
      return 'Your Thera wallet application is pending. Please check back later';
    }
    if (!isWalletCreated && hasBridgeAccount) {
      return 'Your Thera wallet application is under processing. Please check back later';
    }
    if (isOnboardingUnderReview) {
      return 'Your Thera wallet application is under review. Please check back later';
    }
    if (isOnboardingNotStarted) {
      return 'Your Thera wallet application is not started. Please start the application';
    }
    return null;
  };

  // Called by CreateCardBanner after successful card creation: refetch user then show PIN setup modal
  const onCardCreated = async ({ shouldPromptPin = false }: { shouldPromptPin?: boolean } = {}) => {
    await fetchUserProfile();
    if (shouldPromptPin) {
      setShowPinSetupModal(true);
    }
  };

  // Opens Bridge card PIN setup URL in a new tab (user has cardAccount after refetch in onCardCreated)
  const handleGoToPinSetup = async () => {
    const customerId = user?.bridgeUserDetails?.customerId;
    const cardAccountId = user?.bridgeUserDetails?.cardAccount?.id;
    if (!customerId || !cardAccountId) {
      theraToast.error({
        message: 'Unable to open PIN setup. Please refresh the page and try again from your card.',
        options: { id: 'pin-setup-missing-card' },
      });
      return;
    }
    try {
      setIsPinSetupLoading(true);
      const res = await theraFernApi.thera.bridge.bridgeApi.cardPinUpdate(customerId, cardAccountId);
      window.open(res.url, '_blank');
    } catch (error) {
      const errorMessage = formatErrorMessage(error, 'Failed to open PIN setup');
      theraToast.error({
        message: errorMessage,
        error: error,
        options: { id: 'failed-to-open-pin-setup' },
      });
    } finally {
      setIsPinSetupLoading(false);
    }
  };

  if (isUserLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Head>
        <title>{getWhiteLabelPageTitle('Thera Payroll | Wallet')}</title>
      </Head>

      <div className="mx-auto my-6 max-w-screen-lg">
        {hasMissingRequirements && <RequiredDocumentsBanner groups={verificationGroups} />}
        {isWalletActive ? (
          <>
            <div className="">
              <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-medium text-neutral-900">Wallet</h1>
              </div>
              {isCardCreated ? (
                <TheraCard onAddToWallet={() => setShowAddToWalletModal(true)} />
              ) : isUserCountrySupported ? (
                <CreateCardBanner
                  isCardEndorsed={isCardEndorsed}
                  isLoadingDetails={isBridgeCustomerPending}
                  onCardCreated={onCardCreated}
                />
              ) : null}
              {isPinNotSet && !showPinSetupModal && (
                <CardPinSetupBanner onSetupPin={handleGoToPinSetup} isLoading={isPinSetupLoading} />
              )}
            </div>
            <WalletWithdrawalMethodsAndBalance />
            {/* Prompt to set up Bridge card PIN right after card creation (rendered here so modal stays visible after refetch) */}
            <BridgeCardPinSetupModal
              open={showPinSetupModal}
              onClose={() => setShowPinSetupModal(false)}
              onGoToPinSetup={handleGoToPinSetup}
              isLoading={isPinSetupLoading}
            />
            <AddToWalletModal open={showAddToWalletModal} onClose={() => setShowAddToWalletModal(false)} />
          </>
        ) : (
          <div className="mx-auto mt-12 flex max-w-sm flex-col items-center justify-center">
            <img src="/images/thera-wallet.png" alt="thera-wallet" className="w-full" />
            <div className="mt-6 flex flex-col gap-2">
              <h3 className="text-2xl font-semibold text-neutral-900">Introducing Thera Wallet</h3>
              <p className="text-sm font-medium text-neutral-600">
                Now hold funds on the Thera wallet, withdraw in crypto. Cards coming soon.
              </p>
              <div className="mt-3 space-y-4">
                {/* Read-only contractors cannot create wallet */}
                {!hasBridgeAccount && !isReadOnlyContractor && (
                  <Link
                    href={isSupportAdminMode ? '#' : ContractorRoutes.WalletOnboarding}
                    className={`${btnClassesBySize['medium']} ${btnClassesByVariant['primary']} mb-2`}
                  >
                    Create Wallet
                  </Link>
                )}
                {isReadOnlyContractor && !hasBridgeAccount && (
                  <p className="text-sm font-medium text-neutral-500">{readOnlyTooltipMessage}</p>
                )}
                {showWalletStatusText && (
                  <div className="rounded-md bg-neutral-100 p-4">
                    <p className="text-sm font-medium text-neutral-600">{getWalletStatusText()}</p>
                    {(isOnboardingRejected || isOnboardingIncomplete) && !isReadOnlyContractor && (
                      <Button onClick={retryOnboarding} className="mt-2">
                        {isOnboardingRejected ? 'Retry' : 'Resume verification'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AuthorizationCheck(withLayout(Wallet, { isNarrowWidth: true, hasSidebar: true }));

const CardPinSetupBanner = ({ onSetupPin, isLoading }: { onSetupPin: () => Promise<void>; isLoading: boolean }) => {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-6 py-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-amber-200 bg-amber-100">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-amber-600"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>
        <div className="flex min-w-0 flex-col">
          <p className="font-medium text-neutral-900">Set up your card PIN</p>
          <p className="mt-1 text-sm text-neutral-500">
            Set up a PIN for your Thera Card to use it at ATMs and for in-person purchases.
          </p>
        </div>
      </div>
      <div className="flex shrink-0 justify-end max-[359px]:w-full">
        <Button variant="primary" onClick={onSetupPin} loading={isLoading} disabled={isLoading}>
          Setup PIN
        </Button>
      </div>
    </div>
  );
};

export const CreateCardBanner = ({
  isCardEndorsed,
  isLoadingDetails,
  onCardCreated,
}: {
  isCardEndorsed: boolean;
  isLoadingDetails: boolean;
  onCardCreated?: (options?: { shouldPromptPin?: boolean }) => Promise<void>;
}) => {
  const { user, isLoading: isUserLoading } = useUser();
  const { theraFernApi } = useTheraFernClient();
  // Read-only contractors cannot create cards
  const { isReadOnlyContractor, readOnlyTooltipMessage } = useReadOnlyContractor();
  const [isCreatingCard, setIsCreatingCard] = useState(false);
  const [showBankDisclosuresModal, setShowBankDisclosuresModal] = useState(false);

  const createCard = async () => {
    if (!user) return;
    try {
      setIsCreatingCard(true);
      await theraFernApi.thera.bridge.bridgeApi.createStripeCard(user.userID, { type: 'virtual' });
      theraToast.success('Your Thera Card has been created!');
      setShowBankDisclosuresModal(false);
      await onCardCreated?.({ shouldPromptPin: false });
    } catch (error) {
      const errorMessage = formatErrorMessage(error, 'Failed to create card');
      theraToast.error({
        message: errorMessage,
        error: error,
        options: {
          id: 'failed-to-create-card',
        },
      });
    } finally {
      setIsCreatingCard(false);
    }
  };

  const handleCreateCardClick = async () => {
    // If the card is endorsed, show the bank disclosures modal
    if (isCardEndorsed) {
      setShowBankDisclosuresModal(true);
    } else {
      setIsCreatingCard(true);
      theraToast.info('Please wait for some time after verifying your details to continue with card creation');
      // call the endorsement kyc link endpoint
      try {
        const res = await theraFernApi.thera.bridge.bridgeApi.retrieveCardsKycLink(user?.userID);
        window.open(res.url, '_blank');
      } catch (error) {
        const errorMessage = formatErrorMessage(error, 'Failed to retrieve kyc link for card endorsement');
        theraToast.error({
          message: errorMessage,
          error: error,
          options: {
            id: 'failed-to-retrieve-cards-kyc-link',
          },
        });
      } finally {
        setIsCreatingCard(false);
      }
    }
  };

  const handleAcceptDisclosures = async () => {
    await createCard();
  };

  const handleCloseModal = () => {
    if (!isCreatingCard) {
      setShowBankDisclosuresModal(false);
    }
  };

  return (
    <div className="mb-6 flex flex-col items-start gap-4 rounded-md bg-[#80C2FF1A] px-4 pt-4 sm:flex-row">
      <div className="w-full sm:w-auto">
        <img src="/images/thera-wallet-card.svg" alt="thera-card" className="w-full max-w-[300px]" />
      </div>
      <div className="flex h-full w-full flex-col justify-between sm:w-auto">
        <div className="flex flex-1 flex-col gap-1">
          <h3 className="mt-2 text-base font-medium text-neutral-900">Create Thera Card</h3>
          <p className="mt-1 text-sm font-medium text-neutral-600">
            Get a virtual card against your wallet balance for worldwide spending.
          </p>
        </div>
        <div className="items-enc my-4 flex">
          <Button
            variant="outline"
            onClick={handleCreateCardClick}
            disabled={isUserLoading || isReadOnlyContractor || isLoadingDetails}
            tooltipContent={isReadOnlyContractor ? readOnlyTooltipMessage : null}
            loading={isCreatingCard || isLoadingDetails}
          >
            {/* as per Akhil, Always show "Create Card", even if the card is endorsed */}
            {isCardEndorsed ? 'Create Card' : 'Create Card'}
          </Button>
        </div>
      </div>

      {/* Bank Disclosures Modal */}
      <BankDisclosuresModal
        open={showBankDisclosuresModal}
        onClose={handleCloseModal}
        onAccept={handleAcceptDisclosures}
        isLoading={isCreatingCard}
      />
    </div>
  );
};

export const TheraCard = ({ onAddToWallet }: { onAddToWallet?: () => void }) => {
  const [isPinUpdating, setIsPinUpdating] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const { user } = useUser();
  const { theraFernApi } = useTheraFernClient();
  const customerId = user?.bridgeUserDetails?.customerId;
  const stripeCardAccount = user?.bridgeUserDetails?.stripeCardAccount;
  const legacyCardAccountId = user?.bridgeUserDetails?.cardAccount?.id;
  const hasStripeCardAccount = Boolean(stripeCardAccount?.id);
  const bridgeCardAccountId = hasStripeCardAccount ? undefined : legacyCardAccountId;
  const transactionCardAccountId = hasStripeCardAccount ? stripeCardAccount?.id : legacyCardAccountId;
  const walletId = user?.bridgeUserDetails?.wallet?.id;
  const addressToDisplay = displayAddress(user?.employeeInformation?.address);

  // Use custom hooks for data fetching
  const { cardDetails, isLoadingCardDetails, fetchSecureCardDetails } = useSecureCardDetails({
    theraFernApi,
    customerId,
    cardAccountId: bridgeCardAccountId,
  });

  const { cardAccount, mutateCardAccount } = useBridgeCardAccount({ customerId, cardAccountId: bridgeCardAccountId });
  const { bridgeWallet, mutateBridgeWallet } = useBridgeGetWallet({ walletId });

  // Wallet may hold USDB and/or USDC; prefer USDB and fall back to USDC (matches WalletBalanceWithdraw).
  const usdbBalance = bridgeWallet?.balances?.find((b) => b?.currency === 'usdb')?.balance;
  const usdcBalance = bridgeWallet?.balances?.find((b) => b?.currency === 'usdc')?.balance;
  const preferredWalletBalance = Number(usdbBalance ?? 0) > 0 ? usdbBalance : usdcBalance;
  const preferredWalletCurrency = Number(usdbBalance ?? 0) > 0 ? 'USDB' : 'USDC';
  const walletBalance =
    Number(usdbBalance ?? 0) > 0 ? usdbBalance : Number(usdcBalance ?? 0) > 0 ? usdcBalance : undefined;
  const hasWalletBalance = Number(walletBalance ?? 0) > 0;
  const walletBalanceDisplay =
    preferredWalletBalance !== undefined
      ? `${Number(preferredWalletBalance ?? 0).toFixed(2)} ${preferredWalletCurrency}`
      : undefined;
  const cardAvailable = cardAccount?.balances?.available;
  // Stripe cards spend straight from the wallet, so the wallet balance is the card balance.
  const cardBalanceDisplay = hasStripeCardAccount
    ? walletBalanceDisplay
    : cardAccount
      ? `${Number(cardAvailable?.amount ?? 0).toFixed(2)} ${(cardAvailable?.currency || 'usd').toUpperCase()}`
      : undefined;

  const isAddFundsDisabled = hasStripeCardAccount || !hasWalletBalance || !customerId || !legacyCardAccountId;
  const addFundsDisabledReason = hasStripeCardAccount
    ? 'This card spends directly from your wallet balance'
    : !hasWalletBalance
      ? 'Your wallet has no balance to transfer'
      : undefined;

  const { transactions, isFetching } = useCardTransactions({
    theraFernApi,
    customerId,
    cardAccountId: transactionCardAccountId,
  });

  // Use fetched card details if available
  const cardNumberInfo = cardDetails;
  const cardNumberSeparated = cardNumberInfo?.card_number.match(/.{1,4}/g);
  const formattedExpiry = formatCardExpiryDate(cardNumberInfo?.expiry_date);
  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

  // Toggle all card details visibility (retry fetch if it previously failed)
  const handleToggleDetails = () => {
    if (!hasStripeCardAccount && !legacyCardAccountId) {
      return;
    }
    // Stripe details are mounted by StripeIssuingCardDetailsDisplay via its own ephemeral key flow.
    if (!hasStripeCardAccount && !cardDetails && !isLoadingCardDetails) {
      fetchSecureCardDetails();
    }
    setShowDetails((prevState) => !prevState);
  };

  const handleManagePINClick = async () => {
    if (!customerId || !bridgeCardAccountId) {
      return;
    }
    try {
      setIsPinUpdating(true);
      const res = await theraFernApi.thera.bridge.bridgeApi.cardPinUpdate(customerId, bridgeCardAccountId);
      // Open the PIN update URL in a new tab, as this url refuses to be opened in an iframe
      window.open(res.url, '_blank');
    } catch (error) {
      const errorMessage = formatErrorMessage(error, 'Failed to update PIN');
      theraToast.error({
        message: errorMessage,
        error: error,
        options: {
          id: 'failed-to-update-pin',
        },
      });
    } finally {
      setIsPinUpdating(false);
    }
  };

  return (
    <div className="mb-6 overflow-hidden rounded-lg border-2 border-neutral-600 bg-neutral-800">
      <div className="flex flex-col gap-4 p-6 md:flex-row lg:flex-col xl:flex-row">
        {/* Stack transactions below the card on lg widths to avoid sidebar squeeze */}
        {/* Left: Card image, name, address (3/5) */}
        <div className="flex w-full min-w-0 flex-col md:flex-[3_3_0%]">
          {hasStripeCardAccount && stripeCardAccount?.id ? (
            <StripeIssuingCardDetailsDisplay
              theraFernApi={theraFernApi}
              userId={user?.userID}
              stripeCardId={stripeCardAccount.id}
              last4={stripeCardAccount.last4}
              showDetails={showDetails}
              onToggleDetails={handleToggleDetails}
              userName={userName}
              addressToDisplay={addressToDisplay}
            />
          ) : (
            <CardDetailsDisplay
              cardNumberSeparated={cardNumberSeparated}
              formattedExpiry={formattedExpiry}
              cvv={cardNumberInfo?.card_security_code}
              showDetails={showDetails}
              isLoadingCardDetails={isLoadingCardDetails}
              onToggleDetails={handleToggleDetails}
              userName={userName}
              addressToDisplay={addressToDisplay}
            />
          )}
        </div>
        {/* Right: Transactions (2/5) */}
        <TransactionsList
          transactions={transactions}
          isFetching={isFetching}
          isPinUpdating={isPinUpdating}
          isManagePinDisabled={!bridgeCardAccountId}
          managePinDisabledReason={
            hasStripeCardAccount ? 'PIN setup is not available for this virtual Stripe card' : undefined
          }
          hideCardActions={hasStripeCardAccount}
          onManagePINClick={handleManagePINClick}
          cardBalanceDisplay={cardBalanceDisplay}
          onAddFundsClick={() => setShowAddFundsModal(true)}
          isAddFundsDisabled={isAddFundsDisabled}
          addFundsDisabledReason={addFundsDisabledReason}
        />
      </div>
      {/* Add to Apple/Google Pay banner */}
      <div className="flex flex-col items-start justify-between gap-3 border-t border-neutral-700 px-6 py-4 sm:flex-row sm:items-center">
        <div className="flex w-full items-center justify-between gap-4 md:w-auto md:justify-start">
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium text-white">Tap to pay everywhere</p>
            <p className="text-xs text-neutral-400">
              Add the card to your preferred wallet.{' '}
              <button
                onClick={onAddToWallet}
                className="font-medium text-white underline transition-colors hover:text-neutral-300"
              >
                View Steps
              </button>
            </p>
          </div>
          <div className="md:flex md:flex-row md:items-center md:gap-2">
            <img src="/images/apple-wallet.png" alt="apple-wallet" className="h-6 w-auto md:h-8" />
            <img src="/images/google-pay.png" alt="apple-wallet" className="mt-1 h-6 w-auto md:mt-0 md:h-8" />
          </div>
        </div>
      </div>
      <AddFundsToCardModal
        open={showAddFundsModal}
        onClose={() => setShowAddFundsModal(false)}
        customerId={customerId}
        cardAccountId={bridgeCardAccountId}
        walletBalance={walletBalance}
        cardBalanceDisplay={cardBalanceDisplay}
        onTransferred={async () => {
          await Promise.all([mutateCardAccount(), mutateBridgeWallet()]);
        }}
      />
    </div>
  );
};
